// Fallback de progresso ("Continuar assistindo") em localStorage.
//
// POR QUE EXISTE: o progresso de reprodução (PlaybackPosition) mora no
// IndexedDB (local-db). Em TVs webOS/Tizen 2017+ a quota de IDB é apertada e o
// SO trata "temporary storage" como EVICTABLE — ao longo de semanas ela esgota
// e os `put` passam a REJEITAR. Resultado no device do dono: "não está salvando
// o andamento" (o Continuar assistindo nunca retoma), porque a escrita falhava
// silenciosamente.
//
// SOLUÇÃO: quando o IDB rejeita, espelhamos o progresso aqui. localStorage:
//  - é SÍNCRONO → sobrevive ao teardown agressivo do player (o `destroy()`/
//    `suspend()` do zapping roda logo após o cleanup; o microtask do put do IDB
//    pode não completar, mas a escrita síncrona aqui já persistiu).
//  - tem quota SEPARADA do IDB (não é a mesma "temporary storage" evictável).
//  - é minúsculo (~5MB) → guardamos só os N registros MAIS RECENTES de
//    progresso (LRU por lastWatchedAt), não o histórico inteiro.
//
// INVARIANTE: NUNCA lança. Qualquer erro de storage (indisponível, cheio, modo
// privado, file:// restrito) é engolido — o player jamais quebra por causa do
// save. O leitor do "Continuar assistindo" mescla esta fonte com o IDB.

import type { PlaybackPosition } from './local-db'

const STORAGE_KEY = 'swipe:wp-fallback:v1'

/** Teto de registros no fallback. localStorage é pequeno; guardamos só os
 *  mais recentes (o "Continuar assistindo" mostra ~20). Overshoot inofensivo. */
export const FALLBACK_MAX_ENTRIES = 40

/** Acesso defensivo ao localStorage — pode nem existir (SSR/teste node) ou
 *  lançar ao ser tocado (alguns WebViews em file://). */
function store(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage
  } catch {
    return null
  }
}

/** Type guard — records vindos do JSON podem estar corrompidos/parciais. */
function isPosition(v: unknown): v is PlaybackPosition {
  if (!v || typeof v !== 'object') return false
  const p = v as Record<string, unknown>
  return (
    typeof p['id'] === 'string' &&
    typeof p['providerScope'] === 'string' &&
    typeof p['type'] === 'string' &&
    typeof p['lastWatchedAt'] === 'number' &&
    typeof p['positionSec'] === 'number' &&
    typeof p['durationSec'] === 'number'
  )
}

function readAll(): PlaybackPosition[] {
  const s = store()
  if (!s) return []
  try {
    const raw = s.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isPosition)
  } catch {
    return []
  }
}

function writeAll(list: PlaybackPosition[]): void {
  const s = store()
  if (!s) return
  try {
    // LRU: mantém só os mais recentes por lastWatchedAt.
    const bounded = [...list]
      .sort((a, b) => b.lastWatchedAt - a.lastWatchedAt)
      .slice(0, FALLBACK_MAX_ENTRIES)
    s.setItem(STORAGE_KEY, JSON.stringify(bounded))
  } catch {
    // localStorage cheio/indisponível — engole (nunca quebra o player).
  }
}

/** Grava/atualiza um registro de progresso no fallback (upsert por id). */
export function fallbackUpsertPosition(record: PlaybackPosition): void {
  const list = readAll()
  const idx = list.findIndex((p) => p.id === record.id)
  if (idx >= 0) list[idx] = record
  else list.push(record)
  writeAll(list)
}

/** Todos os registros de progresso do scope (provider+profile) atual. */
export function fallbackReadPositions(scope: string): PlaybackPosition[] {
  return readAll().filter((p) => p.providerScope === scope)
}

/** Lookup de um registro por id scopado (`scope::rawId`). */
export function fallbackReadPosition(id: string): PlaybackPosition | undefined {
  return readAll().find((p) => p.id === id)
}

/** Todos os registros do fallback, de TODOS os scopes (migração de limpeza: os
 *  carimbos podem estar em qualquer perfil do device). */
export function fallbackReadAllPositions(): PlaybackPosition[] {
  return readAll()
}

/** Remove vários registros num write só (a lista inteira é reescrita a cada
 *  remoção — em lote evita N reescritas). No-op se nenhum existir. */
export function fallbackRemovePositions(ids: ReadonlyArray<string>): void {
  if (ids.length === 0) return
  const drop = new Set(ids)
  const list = readAll()
  const next = list.filter((p) => !drop.has(p.id))
  if (next.length !== list.length) writeAll(next)
}

/** Remove um registro (ex.: IDB voltou a funcionar e virou fonte canônica,
 *  ou o item foi deletado/limpo). No-op se não existir. */
export function fallbackRemovePosition(id: string): void {
  const list = readAll()
  const next = list.filter((p) => p.id !== id)
  if (next.length !== list.length) writeAll(next)
}

/** Limpa todos os registros de um scope EXATO (`p:CODE::pf:ID`). Usado por
 *  "Limpar histórico" pra o fallback não ressuscitar itens já apagados do IDB. */
export function fallbackClearScope(scope: string): void {
  const list = readAll()
  const next = list.filter((p) => p.providerScope !== scope)
  if (next.length !== list.length) writeAll(next)
}

/** Limpa os registros de TODOS os perfis de um provider (prefixo
 *  `${providerPart}::`). Espelha o corte por prefixo do IDB em
 *  `clearPersonalDataForProvider`. */
export function fallbackClearProviderPrefix(providerPart: string): void {
  const prefix = `${providerPart}::`
  const list = readAll()
  const next = list.filter((p) => !p.providerScope.startsWith(prefix))
  if (next.length !== list.length) writeAll(next)
}

/** Zera o bucket inteiro (factory reset / hard reset do local-db). */
export function fallbackClearAll(): void {
  const s = store()
  if (!s) return
  try {
    s.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}
